import { useNavigate } from "react-router-dom";
import { Cloud, Plane, BarChart3, Settings, LogOut, Lock, Film, Radar } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";

const NSAS_LOGO = "https://customer-assets.emergentagent.com/job_preflight-weather/artifacts/t3bj072m_NSAS-RGB-zilver-baseline.png";

const MenuItem = ({ icon: Icon, title, subtitle, onClick, color, testId, restricted }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="menu-card group"
    style={{ '--accent-color': color }}
  >
    {restricted && (
      <div className="menu-restricted-badge">
        <Lock size={12} />
      </div>
    )}
    <div className="menu-icon-wrapper" style={{ background: `${color}15`, borderColor: `${color}30` }}>
      <Icon size={48} strokeWidth={1.5} style={{ color }} />
    </div>
    <h3 className="menu-title">{title}</h3>
    <p className="menu-subtitle">{subtitle}</p>
    <div className="menu-hover-indicator" style={{ background: color }} />
  </button>
);

export default function HomePage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const handleLogout = () => {
    // Clear all auth (both localStorage and sessionStorage)
    localStorage.removeItem("nsas_authenticated");
    sessionStorage.removeItem("nsas_admin_auth");
    sessionStorage.removeItem("nsas_admin_attempts");
    sessionStorage.removeItem("nsas_admin_lockout");
    window.location.reload();
  };

  const menuItems = [
    {
      icon: Cloud,
      title: "Weather Brief",
      subtitle: "METAR, TAF & flight planning",
      path: "/brief",
      color: "#3B82F6",
      testId: "nav-weather-brief",
      restricted: false
    },
    {
      icon: Plane,
      title: "Ops Dashboard",
      subtitle: "Live flights & operations",
      path: "/ops",
      color: "#10B981",
      testId: "nav-ops-dashboard",
      restricted: false
    },
    {
      icon: Film,
      title: "Video Library",
      subtitle: "Mission recordings & replay",
      path: "/video-library",
      color: "#EC4899",
      testId: "nav-video-library",
      restricted: false
    },
    {
      icon: Radar,
      title: "Aircraft Tracker",
      subtitle: "Live ADS-B tracking",
      path: "/tracker",
      color: "#F97316",
      testId: "nav-aircraft-tracker",
      restricted: false
    },
    {
      icon: BarChart3,
      title: "Fleet Status",
      subtitle: "Contract hours & performance",
      path: "/fleet",
      color: "#8B5CF6",
      testId: "nav-fleet-status",
      restricted: true
    },
    {
      icon: Settings,
      title: "Admin",
      subtitle: "System configuration",
      path: "/admin",
      color: "#F59E0B",
      testId: "nav-admin",
      restricted: true
    }
  ];

  return (
    <div className={`home-container ${theme}`} data-testid="home-page">
      {/* Background effects */}
      <div className="home-bg-gradient" />
      <div className="home-bg-pattern" />
      
      {/* Header */}
      <header className="home-header">
        <img 
          src={NSAS_LOGO} 
          alt="NSAS - North Sea Aviation Services" 
          className="home-logo"
        />
        <div className="home-header-actions">
          <ThemeToggle />
          <button 
            onClick={handleLogout}
            className="logout-btn"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="home-main">
        <div className="home-title-section">
          <h1 className="home-title">NSAS TOOLBOX</h1>
          <p className="home-subtitle">Select a module to continue</p>
        </div>

        <div className="menu-grid">
          {menuItems.map((item) => (
            <MenuItem
              key={item.path}
              icon={item.icon}
              title={item.title}
              subtitle={item.subtitle}
              onClick={() => navigate(item.path)}
              color={item.color}
              testId={item.testId}
              restricted={item.restricted}
            />
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="home-footer">
        <p>North Sea Aviation Services</p>
      </footer>
    </div>
  );
}
